/*
 * Inventory (r16, NecroGrowth docs/01-inventory/01.4-phase1-spec.md section 6): the pure logic behind the ONE
 * reusable InventoryTable - column configs per tab, client-side sort / filter / group-by over the rows the server
 * returned (already scoped to the viewer), saved views in localStorage, and the server params a view maps to.
 *
 * No React and no network here, so every rule is unit-tested (inventoryTable.test.js).
 */

// ---------------------------------------------------------------------------------------------- columns

/*
 * A column: `key` (unique in the table), `label`, `kind` (text | longtext | int | double | bool | date | datetime |
 * enum | ref | chips | count | secret | liveness), `field` (the server field it reads/writes, if any), `ref` (lookup
 * table for a ref column), `link` (the row.links key holding its chip(s)), `editable` (inline edit allowed for the
 * admin), `adminOnly`, `width` (px hint), `groupable`, `hidden` (off by default), `format` (storage | duration: how
 * an int column reads).
 *
 * Phase 2: `liveness` reads the server's derived row.links.liveness / lastSeenMin / missingAlert (phones) or
 * row.links.online / lastSeenMin (PCs) - the phone's last launcher report, "online", "missing" (its PC reports, the
 * phone does not), "offline" or "unknown" (never reported).
 */
export const TABS = [
  { key: 'accounts', label: 'Accounts' },
  { key: 'groups', label: 'Groups' },
  { key: 'phones', label: 'Phones' },
  { key: 'network', label: 'Network' },
  { key: 'clients', label: 'Clients' },
  // Phase 3: views of their own (not an InventoryTable): the Problems board and Capacity.
  { key: 'problems', label: 'Problems', special: true },
  { key: 'capacity', label: 'Capacity', special: true },
  { key: 'history', label: 'History' },
];

/** Sub-views shown as a segmented control inside a tab. `table` is the server table they read. */
export const SUBVIEWS = {
  phones: [
    { key: 'phones', label: 'Phones', table: 'phones' },
    { key: 'profiles', label: 'Profiles', table: 'profiles' },
    { key: 'hosts', label: 'PCs', table: 'hosts', adminOnly: true },
  ],
  history: [
    { key: 'history', label: 'Changes', table: 'history' },
    { key: 'secret-access', label: 'Secret access', table: 'secret-access' },
    // Phase 3: every assign / move / release with Undo (admin).
    { key: 'assignments', label: 'Assignments', table: 'assignments', special: true },
  ],
};

const t = (key, label, extra = {}) => ({ key, label, kind: 'text', field: key, editable: true, ...extra });

/**
 * Phase 2 liveness states, in the order the filter offers them. "unreported" (Phase 3 review should-fix 3): its PC's
 * launcher sees the phone but may not report it (another client's phone, or one holding another client's account) - it
 * is not missing.
 */
export const LIVENESS = ['online', 'missing', 'unreported', 'offline', 'unknown'];
/**
 * Phase 2 review should-fix 5: a liveness filter value matching the server's missingAlert only (missing AND in service:
 * status active or empty) - "missing" alone also lists spare / repair / retired phones that are expected to be away.
 */
export const MISSING_ALERT = 'missing (in service)';

export const COLUMNS = {
  accounts: [
    t('username', 'Username', { width: 170 }),
    { key: 'platform', label: 'Platform', kind: 'enum', field: 'platform', editable: true, groupable: true, width: 110 },
    { key: 'status', label: 'Status', kind: 'enum', field: 'status', editable: true, groupable: true, width: 120 },
    t('statusReason', 'Reason', { width: 160 }),
    { key: 'client', label: 'Client', kind: 'ref', field: 'clientId', ref: 'clients', link: 'client', editable: true, groupable: true, width: 150 },
    { key: 'network', label: 'Network', kind: 'ref', field: 'networkId', ref: 'network', link: 'network', editable: true, groupable: true, width: 150 },
    { key: 'groups', label: 'Groups', kind: 'chips', link: 'groups', groupable: true, width: 180 },
    { key: 'phones', label: 'Phone', kind: 'chips', link: 'phones', width: 170 },
    { key: 'profiles', label: 'Profile', kind: 'chips', link: 'profiles', width: 170, hidden: true },
    { key: 'slot', label: 'Slot', kind: 'ref', field: 'slotId', ref: 'slot', link: 'slot', editable: true, adminOnly: true, width: 150 },
    { key: 'credential', label: 'Login', kind: 'secret', width: 120 },
    { key: 'acquiredAt', label: 'Acquired', kind: 'date', field: 'acquiredAt', editable: true, width: 120, hidden: true },
    { key: 'statusAt', label: 'Status since', kind: 'datetime', field: 'statusAt', width: 150, hidden: true },
    { key: 'notes', label: 'Notes', kind: 'longtext', field: 'notes', editable: true, width: 220 },
    { key: 'updatedAt', label: 'Updated', kind: 'datetime', field: 'updatedAt', width: 150, hidden: true },
  ],
  groups: [
    t('name', 'Name', { width: 180 }),
    t('colour', 'Colour', { width: 100 }),
    t('purpose', 'Purpose', { width: 220 }),
    { key: 'client', label: 'Client', kind: 'ref', field: 'clientId', ref: 'clients', link: 'client', editable: true, groupable: true, width: 150 },
    { key: 'accounts', label: 'Accounts', kind: 'count', link: 'accounts', width: 100 },
    { key: 'notes', label: 'Notes', kind: 'longtext', field: 'notes', editable: true, width: 220 },
  ],
  phones: [
    t('serial', 'Serial', { width: 150 }),
    t('label', 'Label', { width: 140 }),
    t('brand', 'Brand', { width: 100, groupable: true }),
    t('model', 'Model', { width: 120, groupable: true }),
    t('androidVersion', 'Android', { width: 90, groupable: true }),
    { key: 'status', label: 'Status', kind: 'enum', field: 'status', editable: true, groupable: true, width: 110 },
    t('location', 'Location', { width: 160 }),
    { key: 'client', label: 'Client', kind: 'ref', field: 'clientId', ref: 'clients', link: 'client', editable: true, groupable: true, width: 150 },
    { key: 'network', label: 'Network', kind: 'ref', field: 'networkId', ref: 'network', link: 'network', editable: true, groupable: true, width: 150 },
    { key: 'host', label: 'PC', kind: 'ref', field: 'hostId', ref: 'hosts', link: 'host', editable: true, adminOnly: true, groupable: true, width: 120 },
    { key: 'profiles', label: 'Profiles', kind: 'chips', link: 'profiles', width: 200 },
    { key: 'accounts', label: 'Accounts', kind: 'chips', link: 'accounts', width: 180 },
    { key: 'liveness', label: 'Seen', kind: 'liveness', link: 'liveness', values: LIVENESS, groupable: true, width: 150 },
    { key: 'batteryPct', label: 'Battery %', kind: 'int', field: 'batteryPct', width: 90 },
    { key: 'charging', label: 'Charging', kind: 'bool', field: 'charging', width: 80, groupable: true },
    { key: 'temperatureC', label: 'Temp °C', kind: 'double', field: 'temperatureC', width: 90 },
    { key: 'freeStorageMb', label: 'Free storage', kind: 'int', field: 'freeStorageMb', format: 'storage', width: 105 },
    { key: 'uptimeSec', label: 'Uptime', kind: 'int', field: 'uptimeSec', format: 'duration', width: 95, hidden: true },
    { key: 'runningSlots', label: 'Running slots', kind: 'text', field: 'runningSlots', adminOnly: true, width: 140 },
    { key: 'lastSeenAt', label: 'Last seen', kind: 'datetime', field: 'lastSeenAt', width: 150 },
    { key: 'adbId', label: 'adb id', kind: 'text', field: 'adbId', adminOnly: true, width: 150, hidden: true },
    t('abi', 'ABI', { width: 110, hidden: true }),
    { key: 'device', label: 'Device row', kind: 'ref', field: 'deviceRowId', ref: 'device', link: 'device', editable: true, adminOnly: true, width: 150, hidden: true },
    { key: 'notes', label: 'Notes', kind: 'longtext', field: 'notes', editable: true, width: 200 },
  ],
  profiles: [
    { key: 'phone', label: 'Phone', kind: 'ref', field: 'phoneId', ref: 'phones', link: 'phone', groupable: true, width: 180 },
    { key: 'androidUserId', label: 'User id', kind: 'int', field: 'androidUserId', width: 80 },
    t('name', 'Name', { width: 110 }),
    { key: 'account', label: 'Account logged in', kind: 'ref', field: 'accountId', ref: 'accounts', link: 'account', editable: true, width: 170 },
    { key: 'igInstalled', label: 'IG', kind: 'bool', field: 'igInstalled', width: 60, groupable: true },
    t('igVersion', 'IG version', { width: 120, groupable: true }),
    { key: 'tiktokInstalled', label: 'TikTok', kind: 'bool', field: 'tiktokInstalled', width: 70, groupable: true },
    t('tiktokVersion', 'TikTok version', { width: 120, hidden: true }),
    t('keyboard', 'Keyboard', { width: 200, hidden: true }),
    { key: 'present', label: 'Present', kind: 'bool', field: 'present', width: 80, groupable: true },
    { key: 'lastSeenAt', label: 'Last seen', kind: 'datetime', field: 'lastSeenAt', width: 150 },
    { key: 'notes', label: 'Notes', kind: 'longtext', field: 'notes', editable: true, width: 200 },
  ],
  hosts: [
    t('label', 'Label', { width: 150 }),
    { key: 'kind', label: 'Kind', kind: 'enum', field: 'kind', editable: true, groupable: true, width: 80 },
    t('hostname', 'Hostname', { width: 140 }),
    t('os', 'OS', { width: 140 }),
    t('launcherVersion', 'Launcher', { width: 100 }),
    t('engineVersion', 'Engine', { width: 100 }),
    { key: 'online', label: 'Online', kind: 'liveness', link: 'liveness', values: ['online', 'offline', 'unknown'], groupable: true, width: 130 },
    { key: 'phones', label: 'Phones', kind: 'chips', link: 'phones', width: 220 },
    { key: 'lastSeenAt', label: 'Last seen', kind: 'datetime', field: 'lastSeenAt', width: 150 },
    t('location', 'Location', { width: 150 }),
    { key: 'notes', label: 'Notes', kind: 'longtext', field: 'notes', editable: true, width: 200 },
  ],
  network: [
    t('name', 'Name', { width: 160 }),
    { key: 'type', label: 'Type', kind: 'enum', field: 'type', editable: true, groupable: true, width: 90 },
    t('provider', 'Provider', { width: 120, groupable: true }),
    t('endpoint', 'Endpoint / SIM / profile', { width: 190 }),
    t('country', 'Country', { width: 90, groupable: true }),
    t('city', 'City', { width: 100, hidden: true }),
    { key: 'status', label: 'Status', kind: 'enum', field: 'status', editable: true, groupable: true, width: 100 },
    { key: 'client', label: 'Client', kind: 'ref', field: 'clientId', ref: 'clients', link: 'client', editable: true, groupable: true, width: 150 },
    { key: 'accounts', label: 'Accounts', kind: 'chips', link: 'accounts', width: 180 },
    { key: 'phones', label: 'Phones', kind: 'chips', link: 'phones', width: 180 },
    { key: 'secret', label: 'Login', kind: 'secret', width: 110 },
    { key: 'notes', label: 'Notes', kind: 'longtext', field: 'notes', editable: true, width: 200 },
  ],
  clients: [
    t('name', 'Name', { width: 170 }),
    t('contact', 'Contact', { width: 170 }),
    t('plan', 'Plan', { width: 130, groupable: true }),
    t('speed', 'Speed', { width: 100 }),
    { key: 'startDate', label: 'Start', kind: 'date', field: 'startDate', editable: true, width: 120 },
    { key: 'status', label: 'Status', kind: 'enum', field: 'status', editable: true, groupable: true, width: 100 },
    { key: 'user', label: 'Login (user)', kind: 'ref', field: 'userId', ref: 'user', link: 'user', editable: true, adminOnly: true, width: 190 },
    { key: 'accounts', label: 'Accounts', kind: 'count', link: 'accounts', width: 90 },
    { key: 'phones', label: 'Phones', kind: 'count', link: 'phones', width: 90 },
    { key: 'notes', label: 'Notes', kind: 'longtext', field: 'notes', editable: true, width: 200 },
  ],
  history: [
    { key: 'at', label: 'When', kind: 'datetime', field: 'at', width: 150 },
    { key: 'target', label: 'Record', kind: 'chips', link: 'target', width: 200 },
    { key: 'entity', label: 'Type', kind: 'text', field: 'entity', groupable: true, width: 90 },
    { key: 'field', label: 'Field', kind: 'text', field: 'field', groupable: true, width: 130 },
    { key: 'oldValue', label: 'Old', kind: 'text', field: 'oldValue', width: 170 },
    { key: 'newValue', label: 'New', kind: 'text', field: 'newValue', width: 170 },
    { key: 'actorType', label: 'By', kind: 'text', field: 'actorType', groupable: true, width: 90 },
    { key: 'actor', label: 'Who', kind: 'text', field: 'actor', groupable: true, width: 190 },
    { key: 'note', label: 'Note', kind: 'text', field: 'note', width: 160, hidden: true },
  ],
  'secret-access': [
    { key: 'at', label: 'When', kind: 'datetime', field: 'at', width: 150 },
    { key: 'target', label: 'Record', kind: 'chips', link: 'target', width: 200 },
    { key: 'kind', label: 'Kind', kind: 'text', field: 'kind', groupable: true, width: 90 },
    { key: 'fields', label: 'Fields', kind: 'text', field: 'fields', width: 220 },
    { key: 'outcome', label: 'Outcome', kind: 'text', field: 'outcome', groupable: true, width: 100 },
    { key: 'userEmail', label: 'Who', kind: 'text', field: 'userEmail', groupable: true, width: 200 },
    // INV-8 (re-review 3 should-fix 3): adminOnly like the server field - a client got the (always blank) column, and
    // after sorting by it, Export CSV sent sort=ip, which the server refuses for a client (400).
    { key: 'ip', label: 'IP', kind: 'text', field: 'ip', width: 120, hidden: true, adminOnly: true },
  ],
};

/** The columns this viewer may see, with server metadata merged in (enum values, read-only flags). */
export function columnsFor(table, meta) {
  const isAdmin = !!meta?.isAdmin;
  const fields = {};
  for (const f of meta?.tables?.[table]?.fields || []) fields[f.name] = f;
  const readOnlyTable = meta?.tables?.[table]?.readOnly !== false;
  return (COLUMNS[table] || [])
    .filter((c) => isAdmin || !c.adminOnly)
    .map((c) => {
      const f = c.field ? fields[c.field] : null;
      const values = f?.values || c.values;
      // Editable only when the column says so, the server lists the field as writable, and the viewer may write.
      const editable = !!c.editable && !readOnlyTable && (!c.field || !f || f.writable !== false);
      // Phase 2: the liveness badge says the server's threshold ("missing > 30 min"), from /meta.
      const extra = c.kind === 'liveness' ? { missingMinutes: Number(meta?.missingMinutes) || 30 } : {};
      return { ...c, values, editable, ...extra };
    });
}

// ---------------------------------------------------------------------------------------------- cell values

/** The value a column shows / sorts / filters / groups by, as plain text (chips -> their labels). */
export function cellText(row, col) {
  if (!row) return '';
  if (col.kind === 'chips') {
    const v = row.links?.[col.link];
    if (Array.isArray(v)) return v.map((c) => c.label).join(', ');
    return v?.label || '';
  }
  if (col.kind === 'count') {
    const v = row.links?.[col.link];
    return v == null ? '' : String(v);
  }
  if (col.kind === 'ref') {
    const chip = row.links?.[col.link];
    if (chip?.label) return chip.label;
    const id = row[col.field];
    return id == null ? '' : `#${id}`;
  }
  if (col.kind === 'secret') {
    const f = col.key === 'credential' ? row.links?.credential : { password: row.hasSecret };
    if (!f) return '';
    return Object.entries(f).filter(([, on]) => on).map(([k]) => k).join(', ');
  }
  if (col.kind === 'bool') {
    const v = col.link ? row.links?.[col.link] : row[col.field];
    return v == null ? '' : v ? 'yes' : 'no';
  }
  if (col.kind === 'liveness') return livenessOf(row).state;
  const v = row[col.field];
  if (v == null) return '';
  if (col.format === 'storage') return formatStorage(v);
  if (col.format === 'duration') return formatDuration(v);
  return String(v);
}

// ---------------------------------------------------------------------------------------------- phase 2: liveness

/**
 * A row's liveness from the server's derived links: {state, minutes, alert}. Phones carry links.liveness; PCs carry
 * links.online (+ lastSeenMin) and read "online" / "offline" ("unknown" when never seen).
 */
export function livenessOf(row) {
  const l = row?.links || {};
  const minutes = l.lastSeenMin == null || l.lastSeenMin === '' ? null : Number(l.lastSeenMin);
  let state = l.liveness;
  if (!state && l.online != null) state = l.online ? 'online' : minutes == null ? 'unknown' : 'offline';
  if (!LIVENESS.includes(state)) state = 'unknown';
  return { state, minutes: Number.isFinite(minutes) ? minutes : null, alert: !!l.missingAlert };
}

/** "just now", "12 min", "3 h 5 min", "2 d 4 h" - how long ago, from whole minutes. */
export function formatAgo(minutes) {
  if (minutes == null || !Number.isFinite(Number(minutes))) return '';
  const m = Math.max(0, Math.floor(Number(minutes)));
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return m % 60 ? `${h} h ${m % 60} min` : `${h} h`;
  const d = Math.floor(h / 24);
  return h % 24 ? `${d} d ${h % 24} h` : `${d} d`;
}

/**
 * "12 min ago", "3 h 5 min ago" - and "just now", never "just now ago" (INV-7b browser drive: the Problems board's
 * Since column read "just now ago" for a fresh hot phone / low battery). '-' when unknown.
 */
export function formatSince(minutes) {
  const a = formatAgo(minutes);
  if (!a) return '-';
  return a === 'just now' ? a : `${a} ago`;
}

/** Free storage in MB as people read it: "850 MB", "10.0 GB". */
export function formatStorage(mb) {
  const n = Number(mb);
  if (!Number.isFinite(n)) return '';
  return n >= 1024 ? `${(n / 1024).toFixed(1)} GB` : `${Math.round(n)} MB`;
}

/** Uptime in seconds: "45 min", "3 h 5 min", "12 d 2 h". */
export function formatDuration(sec) {
  const n = Number(sec);
  if (!Number.isFinite(n)) return '';
  return formatAgo(Math.floor(n / 60)) === 'just now' ? '< 1 min' : formatAgo(Math.floor(n / 60));
}

/** Phones the server flags missing (in service, not reported for > the threshold while their PC reports). */
export function missingAlerts(rows) {
  return (rows || []).filter((r) => !r?.archivedAt && livenessOf(r).alert);
}

/** A sortable value: numbers as numbers, dates as ISO text, everything else lower-cased text. */
export function sortValue(row, col) {
  if (col.kind === 'liveness') {
    // Most recently seen first in ascending order; never-seen rows sort last either way.
    return livenessOf(row).minutes;
  }
  if (col.kind === 'int' || col.kind === 'double' || col.kind === 'count') {
    const raw = col.kind === 'count' ? row.links?.[col.link] : row[col.field];
    return raw == null || raw === '' ? null : Number(raw);
  }
  if (col.kind === 'datetime' || col.kind === 'date') {
    return row[col.field] || null;
  }
  const s = cellText(row, col);
  return s === '' ? null : s.toLowerCase();
}

// ---------------------------------------------------------------------------------------------- sort / filter / group

/** sort: [{key, dir: 'asc'|'desc'}], stable, empty values last whatever the direction. */
export function sortRows(rows, columns, sort) {
  if (!sort || sort.length === 0) return rows;
  const cols = sort.map((s) => ({ col: columns.find((c) => c.key === s.key), dir: s.dir === 'desc' ? -1 : 1 }))
    .filter((s) => s.col);
  if (cols.length === 0) return rows;
  return rows
    .map((row, i) => ({ row, i }))
    .sort((a, b) => {
      for (const { col, dir } of cols) {
        const va = sortValue(a.row, col);
        const vb = sortValue(b.row, col);
        if (va == null && vb == null) continue;
        if (va == null) return 1;
        if (vb == null) return -1;
        if (va < vb) return -1 * dir;
        if (va > vb) return 1 * dir;
      }
      return a.i - b.i;
    })
    .map((x) => x.row);
}

/** Click on a header: plain click sorts by that column alone (asc -> desc -> off); shift-click adds / flips a key. */
export function nextSort(sort, key, additive) {
  const cur = (sort || []).find((s) => s.key === key);
  if (!additive) {
    if (!cur) return [{ key, dir: 'asc' }];
    if (cur.dir === 'asc') return [{ key, dir: 'desc' }];
    return [];
  }
  if (!cur) return [...(sort || []), { key, dir: 'asc' }];
  if (cur.dir === 'asc') return sort.map((s) => (s.key === key ? { key, dir: 'desc' } : s));
  return sort.filter((s) => s.key !== key);
}

/**
 * filters: {colKey: text}. Text-like columns match "contains" (case-insensitive); enum / bool match exactly;
 * "(empty)" matches an empty cell. q searches every visible column.
 */
export function filterRows(rows, columns, filters, q) {
  const active = Object.entries(filters || {}).filter(([, v]) => v != null && String(v).trim() !== '');
  const needle = (q || '').trim().toLowerCase();
  if (active.length === 0 && !needle) return rows;
  const byKey = Object.fromEntries(columns.map((c) => [c.key, c]));
  return rows.filter((row) => {
    for (const [key, raw] of active) {
      const col = byKey[key];
      if (!col) continue;
      const want = String(raw).trim().toLowerCase();
      const have = cellText(row, col).toLowerCase();
      if (want === '(empty)') {
        if (have !== '') return false;
      } else if (col.kind === 'liveness' && want === MISSING_ALERT) {
        if (!livenessOf(row).alert) return false;
      } else if (col.kind === 'enum' || col.kind === 'bool' || col.kind === 'liveness') {
        if (have !== want) return false;
      } else if (!have.includes(want)) {
        return false;
      }
    }
    if (needle) {
      return columns.some((c) => cellText(row, c).toLowerCase().includes(needle));
    }
    return true;
  });
}

/**
 * Group by a column: [{key, label, rows}] in first-seen order (after sorting), "(none)" for empty. A chips column
 * puts a row under EACH of its chips (an account in two groups shows in both) - the Airtable behaviour.
 */
export function groupRows(rows, columns, groupBy) {
  const col = columns.find((c) => c.key === groupBy);
  if (!col) return [{ key: '__all', label: null, rows }];
  const groups = new Map();
  const add = (k, row) => {
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k).push(row);
  };
  for (const row of rows) {
    if (col.kind === 'chips' && Array.isArray(row.links?.[col.link])) {
      const chips = row.links[col.link];
      if (chips.length === 0) add('(none)', row);
      for (const c of chips) add(c.label, row);
    } else {
      const v = cellText(row, col);
      add(v === '' ? '(none)' : v, row);
    }
  }
  return [...groups.entries()].map(([label, rs]) => ({ key: label, label, rows: rs }));
}

/** Everything the table shows, in one call. */
export function viewRows(rows, columns, view) {
  const filtered = filterRows(rows, columns, view?.filters, view?.q);
  const sorted = sortRows(filtered, columns, view?.sort);
  return groupRows(sorted, columns, view?.groupBy);
}

// ---------------------------------------------------------------------------------------------- server params

/**
 * The server-side filters a view maps to (for the CSV export of "the current filter"): q, and the column filters
 * whose server meaning is identical - text contains and enum equality on a real field. Ref / chips filters are by
 * label, which the server cannot do, so they stay client-side.
 */
export function serverParams(columns, view, base = {}, fields = null, isAdmin = true) {
  const out = { ...base };
  if (view?.q && view.q.trim()) out.q = view.q.trim();
  for (const [key, raw] of Object.entries(view?.filters || {})) {
    const col = columns.find((c) => c.key === key);
    const v = raw == null ? '' : String(raw).trim();
    if (!col || !col.field || v === '' || v === '(empty)') continue;
    if (['text', 'longtext', 'enum'].includes(col.kind)) out[`f.${col.field}`] = v;
  }
  const sort = exportSortParam(columns, view, fields, isAdmin);
  if (sort) out.sort = sort;
  return out;
}

/** The row's own columns: the server sorts by them on every archivable table, and /meta never lists them. */
const BASE_SORT_FIELDS = ['id', 'createdAt', 'updatedAt', 'archivedAt'];

/** Column kinds the server can sort by its own field (refs sort by id there, chips / counts are computed). */
const SERVER_SORTABLE = ['text', 'enum', 'int', 'double', 'date', 'datetime', 'bool'];

/**
 * INV-7b browser drive: Export CSV wrote the rows in the server's default order whatever the table showed; it now
 * sends the view's sort ("status,-username") for every sort key the server can sort by itself - a plain field column
 * (never a link, a chip list, a count or a long text: the server refuses those, and a client may not sort by an
 * admin-only field). {@code fields}: the table's server field metadata (meta.tables[t].fields), to skip LONGTEXT
 * fields that V3 shows as text (History old / new value) and - INV-8, re-review 3 should-fix 3 - for a CLIENT viewer
 * ({@code isAdmin} false), every field its meta does not list: the server leaves admin-only fields out of a client's
 * meta and refuses a client's sort by them with 400 (the row's base columns id / createdAt / updatedAt / archivedAt are
 * never in the field list and always sortable). Null when nothing to send.
 */
export function exportSortParam(columns, view, fields = null, isAdmin = true) {
  const kindOf = {};
  for (const f of fields || []) kindOf[f.name] = f.kind;
  const known = !isAdmin && Array.isArray(fields) && fields.length
    ? new Set([...fields.map((f) => f.name), ...BASE_SORT_FIELDS]) : null;
  const parts = [];
  for (const s of view?.sort || []) {
    const col = columns.find((c) => c.key === s?.key);
    if (!col || !col.field || !SERVER_SORTABLE.includes(col.kind)) continue;
    if (known && !known.has(col.field)) continue;
    if (kindOf[col.field] === 'LONGTEXT') continue;
    parts.push(`${s.dir === 'desc' ? '-' : ''}${col.field}`);
  }
  return parts.length ? parts.join(',') : null;
}

/** "Client" for clientId: the column's label when a column shows the field, else the field name made readable. */
export function fieldLabel(columns, name) {
  const col = (columns || []).find((c) => c.field === name);
  if (col?.label) return col.label;
  const words = String(name || '').replace(/Id$/, '').replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : String(name || '');
}

// ---------------------------------------------------------------------------------------------- saved views

/**
 * INV-7b browser drive: saved views and the last working state are kept PER LOGIN. They were kept per browser, so the
 * next login on the same browser (a client after the admin) opened with the admin's search, filters and saved view
 * names ("0 shown" - and the admin's view names can name other clients). InventoryPage sets the scope to the viewer's
 * email; the old unscoped keys are removed once.
 */
let storageScope = '';

export function setStorageScope(email) {
  const next = String(email || '').trim().toLowerCase();
  if (next === storageScope) return;
  storageScope = next;
  if (next) dropUnscopedState();
}

export function getStorageScope() {
  return storageScope;
}

function dropUnscopedState() {
  try {
    const ls = window.localStorage;
    const legacy = [];
    for (let i = 0; i < ls.length; i++) {
      const k = ls.key(i);
      // Unscoped = "necro.inventory.<views|state>.<table>" with no "@" (a scoped key carries the email).
      if (k && /^necro\.inventory\.(views|state)\.[^@]+$/.test(k)) legacy.push(k);
    }
    legacy.forEach((k) => ls.removeItem(k));
  } catch (e) {
    // storage blocked: nothing to clean
  }
}

const scoped = (kind, table) => (storageScope ? `necro.inventory.${kind}.${storageScope}.${table}` : `necro.inventory.${kind}.${table}`);
const VIEWS_KEY = (table) => scoped('views', table);
const STATE_KEY = (table) => scoped('state', table);

export const EMPTY_VIEW = { q: '', filters: {}, sort: [], groupBy: '', hidden: null };

function readJson(key, fallback) {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    const v = JSON.parse(raw);
    return v == null ? fallback : v;
  } catch (e) {
    return fallback;
  }
}

function writeJson(key, value) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (e) {
    return false;
  }
}

/** Saved views of a table: [{name, view}]. */
export function loadViews(table) {
  const v = readJson(VIEWS_KEY(table), []);
  return Array.isArray(v) ? v.filter((x) => x && typeof x.name === 'string' && x.view) : [];
}

/** Saves (or replaces by name) a view; returns the new list. Never throws (storage may be blocked). */
export function saveView(table, name, view) {
  const clean = String(name || '').trim();
  if (!clean) return loadViews(table);
  const list = loadViews(table).filter((x) => x.name !== clean);
  list.push({ name: clean, view: normaliseView(view) });
  list.sort((a, b) => a.name.localeCompare(b.name));
  // Storage blocked (private window, policy): say so by returning what is really stored, not a phantom list.
  return writeJson(VIEWS_KEY(table), list) ? list : loadViews(table);
}

export function deleteView(table, name) {
  const list = loadViews(table).filter((x) => x.name !== name);
  return writeJson(VIEWS_KEY(table), list) ? list : loadViews(table);
}

/** The last working state of a table (restored on the next visit). */
export function loadState(table) {
  return normaliseView(readJson(STATE_KEY(table), EMPTY_VIEW));
}

export function saveState(table, view) {
  return writeJson(STATE_KEY(table), normaliseView(view));
}

export function normaliseView(v) {
  const view = v && typeof v === 'object' ? v : {};
  return {
    q: typeof view.q === 'string' ? view.q : '',
    filters: view.filters && typeof view.filters === 'object' ? view.filters : {},
    sort: Array.isArray(view.sort) ? view.sort.filter((s) => s && s.key) : [],
    groupBy: typeof view.groupBy === 'string' ? view.groupBy : '',
    hidden: Array.isArray(view.hidden) ? view.hidden : null,
  };
}

/** Hidden column keys: the view's own list, else every column marked hidden by default. */
export function hiddenColumns(columns, view) {
  if (Array.isArray(view?.hidden)) return new Set(view.hidden);
  return new Set(columns.filter((c) => c.hidden).map((c) => c.key));
}

// ---------------------------------------------------------------------------------------------- editing

/** Converts an editor's string to what the server expects for the column (null clears). */
export function toServerValue(col, raw) {
  if (raw === undefined) return undefined;
  if (raw === null) return null;
  if (col.kind === 'bool') return raw === true || raw === 'true';
  const s = String(raw).trim();
  if (s === '') return null;
  if (col.kind === 'int') return Number.isNaN(parseInt(s, 10)) ? s : parseInt(s, 10);
  if (col.kind === 'double') return Number.isNaN(parseFloat(s)) ? s : parseFloat(s);
  if (col.kind === 'ref') return Number.isNaN(Number(s)) ? s : Number(s);
  return s;
}

/** Applies an edit to a row copy for the optimistic update (the server reply replaces it). */
export function applyLocalEdit(row, col, value, chip) {
  const next = { ...row, [col.field]: value };
  if (col.kind === 'ref') {
    const links = { ...(row.links || {}) };
    if (value == null) delete links[col.link];
    else if (chip) links[col.link] = chip;
    next.links = links;
  }
  return next;
}

/** Columns the bulk editor offers: editable, with a real field, and not unique per row. */
const UNIQUE = new Set(['username', 'serial', 'name', 'androidUserId', 'slotId', 'bmInstallId']);
export function bulkColumns(columns) {
  return columns.filter((c) => c.editable && c.field && !UNIQUE.has(c.field));
}

/** Short relative-free date text: "2026-10-03 14:05". */
export function formatDateTime(v) {
  if (!v) return '';
  const s = String(v);
  return s.length >= 16 ? `${s.slice(0, 10)} ${s.slice(11, 16)}` : s;
}
